import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CameraCapture } from '@/components/CameraCapture';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { AlertTriangle, MapPin, Send, Loader2, Navigation, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const VIOLATION_TYPES = [
  'Wrong Parking', 'Signal Jumping', 'No Helmet', 'Triple Riding',
  'Dangerous Driving', 'Illegal U-turn', 'Overspeeding', 'Wrong Way Driving',
  'Using Mobile While Driving', 'No Seatbelt', 'Overloading', 'Other'
];

export default function ReportViolationPage() {
  const { authAxios, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [location, setLocation] = useState(null);
  const [locLoading, setLocLoading] = useState(false);
  const [locError, setLocError] = useState('');
  const [category, setCategory] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [description, setDescription] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [legalConfirmed, setLegalConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [step, setStep] = useState(1); // 1=capture, 2=location, 3=details

  const getLocation = useCallback(() => {
    setLocLoading(true);
    setLocError('');
    if (!navigator.geolocation) {
      setLocError('Geolocation not supported');
      setLocLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          altitude: pos.coords.altitude,
          accuracy: pos.coords.accuracy,
          speed: pos.coords.speed,
        });
        setLocLoading(false);
        setStep(3);
      },
      (err) => {
        setLocError('Location access is required. Please enable GPS.');
        setLocLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const handleCapture = (captureData) => {
    setMedia(captureData);
    if (captureData) setStep(2);
  };

  const detectPlate = async () => {
    if (!media?.data) return;
    setDetecting(true);
    try {
      const res = await authAxios.post('/ai/detect-plate', { image_data: media.data });
      if (res.data.detected) {
        setVehicleNumber(res.data.plate_number);
        toast.success(`Plate detected: ${res.data.plate_number}`);
      } else {
        toast.info('No plate number detected in the image');
      }
    } catch {
      toast.error('Plate detection failed');
    }
    setDetecting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!media) { toast.error('Please capture evidence first'); return; }
    if (!location) { toast.error('Location is required'); return; }
    if (!category) { toast.error('Select violation type'); return; }
    if (!legalConfirmed) { toast.error('Please confirm the legal notice'); return; }

    setSubmitting(true);
    try {
      const ua = navigator.userAgent;
      const isMobile = /mobile/i.test(ua);
      await authAxios.post('/reports', {
        report_type: 'violation',
        category,
        description,
        vehicle_number: vehicleNumber,
        latitude: location.latitude,
        longitude: location.longitude,
        altitude: location.altitude,
        gps_accuracy: location.accuracy,
        speed: location.speed,
        device_type: isMobile ? 'mobile' : 'desktop',
        browser: ua.substring(0, 100),
        anonymous,
        legal_confirmed: legalConfirmed,
        media_data: media.data,
        media_type: media.type,
      });
      toast.success('Violation report submitted successfully!');
      navigate('/my-reports');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    }
    setSubmitting(false);
  };

  if (authLoading) return <div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="report-violation-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Report Traffic Violation</h1>
            <p className="text-sm text-muted-foreground">Capture evidence and submit to traffic police</p>
          </div>
        </div>
        {/* Progress */}
        <div className="flex gap-2 mt-6">
          {['Capture', 'Location', 'Details'].map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors ${step > i ? 'bg-primary' : step === i + 1 ? 'bg-primary/50' : 'bg-muted'}`} />
              <span className="text-xs text-muted-foreground mt-1 block">{s}</span>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Camera */}
        <Card data-testid="step-capture">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">1</span>
              Capture Evidence
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CameraCapture onCapture={handleCapture} mode="photo" />
            {media && (
              <Button type="button" variant="outline" size="sm" className="mt-3 gap-2" onClick={detectPlate} disabled={detecting} data-testid="detect-plate-btn">
                {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                AI Detect Plate Number
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Location */}
        {step >= 2 && (
          <Card className="animate-fade-in-up" data-testid="step-location">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">2</span>
                Verify Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!location ? (
                <div className="text-center py-6 space-y-4">
                  <MapPin className="w-10 h-10 text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground">To ensure accurate reporting, location access is required.</p>
                  {locError && <p className="text-sm text-destructive">{locError}</p>}
                  <Button type="button" onClick={getLocation} disabled={locLoading} className="gap-2" data-testid="get-location-btn">
                    {locLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                    {locLoading ? 'Getting Location...' : 'Enable Location'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-4 rounded-lg bg-muted">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground block text-xs">Latitude</span>
                        <span className="font-mono font-medium">{location.latitude.toFixed(6)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block text-xs">Longitude</span>
                        <span className="font-mono font-medium">{location.longitude.toFixed(6)}</span>
                      </div>
                      {location.accuracy && (
                        <div>
                          <span className="text-muted-foreground block text-xs">Accuracy</span>
                          <span className="font-mono font-medium">{location.accuracy.toFixed(1)}m</span>
                        </div>
                      )}
                      {location.altitude && (
                        <div>
                          <span className="text-muted-foreground block text-xs">Altitude</span>
                          <span className="font-mono font-medium">{location.altitude.toFixed(1)}m</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />Is this the correct location of the violation?
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Details */}
        {step >= 3 && (
          <Card className="animate-fade-in-up" data-testid="step-details">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">3</span>
                Violation Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Violation Type *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="violation-type-select">
                    <SelectValue placeholder="Select violation type" />
                  </SelectTrigger>
                  <SelectContent>
                    {VIOLATION_TYPES.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Vehicle Number</Label>
                <Input
                  placeholder="e.g. KA-32-AB-1234"
                  value={vehicleNumber}
                  onChange={e => setVehicleNumber(e.target.value)}
                  className="font-mono uppercase"
                  data-testid="vehicle-number-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Describe the violation..."
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  data-testid="description-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="anonymous"
                  checked={anonymous}
                  onCheckedChange={setAnonymous}
                  data-testid="anonymous-checkbox"
                />
                <Label htmlFor="anonymous" className="text-sm cursor-pointer">Submit anonymously</Label>
              </div>

              {/* Legal Notice */}
              <div className="p-4 rounded-lg border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5">
                <p className="text-sm font-medium text-foreground mb-3">
                  Submitting false traffic violation reports may lead to account suspension and legal action.
                </p>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="legal"
                    checked={legalConfirmed}
                    onCheckedChange={setLegalConfirmed}
                    data-testid="legal-checkbox"
                  />
                  <Label htmlFor="legal" className="text-sm cursor-pointer">
                    I confirm this report is genuine and accurate
                  </Label>
                </div>
              </div>

              <Button type="submit" className="w-full font-bold uppercase tracking-wide gap-2" disabled={submitting} size="lg" data-testid="submit-violation-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? 'Submitting...' : 'Submit Report'}
              </Button>
            </CardContent>
          </Card>
        )}
      </form>
    </div>
  );
}
